// Helpers shared by LoansPage / LoanDetailPage for working with package
// (LoanRequest.fields) data generically, driven by PACKAGE_FIELD_KEYS.
import type { PackageFields, PackageType } from '@app/shared';
import { PACKAGE_FIELD_KEYS, UPP_WITHOUT_CO } from '@app/shared';

// The CPC claim's fields, hosted by UPP or Charge Off since CPC stopped being a
// package type of its own (#51).
const CPC_KEYS: (keyof PackageFields)[] = [
  'cpcSubmittedDate', 'cpcSubmittedAmount',
  'cpcReceivedDate', 'cpcReceivedAmount', 'cpcComments',
];

// PackageFields keys whose value is numeric.
export const NUMERIC_FIELD_KEYS = new Set<keyof PackageFields>([
  'disallowedInterest',
  'oicAmountOffered', 'oicAmountApproved',
  'litFeesApprovedToDate', 'litFeesNotApprovedToDate', 'litTotalBudgetRequested', 'litLegalExpenses',
  'cpcSubmittedAmount', 'cpcReceivedAmount',
  'expensesAmount', 'chargeOffExpensesSubmitted',
]);

// PackageFields keys whose value is boolean.
export const BOOLEAN_FIELD_KEYS = new Set<keyof PackageFields>([
  'rtsModificationCompleted',
  'collateralLiquidated', 'expensesIncurred', 'referToTreasury',
]);

// PackageFields keys that are free-text strings (not dates, not enum selects).
export const TEXT_FIELD_KEYS = new Set<keyof PackageFields>([
  'cpcComments', 'rtsSbaDecision', 'oicOfferor', 'extensionLength',
]);

// Litigation fees are exhaustive (approved by SBA + not approved), so the plan's
// "Total Fees & Costs to Date" is their sum rather than a stored field — one
// authoritative source, and a total that cannot drift from its parts.
export function litTotalFeesDisplay(f: Record<string, unknown>): string {
  const n = litTotalFeesToDate(f);
  return n == null ? '—' : '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function litTotalFeesToDate(f: Record<string, unknown>): number | null {
  const a = f.litFeesApprovedToDate, b = f.litFeesNotApprovedToDate;
  if ((a ?? '') === '' && (b ?? '') === '') return null;
  return (Number(a) || 0) + (Number(b) || 0);
}

// Fields that only apply when another field holds a particular value. The generic
// editor hides them otherwise, so a form can't keep collecting an answer to a
// question it stopped asking. Add a rule here rather than special-casing a form.
// True when the CPC group applies. Each host says so with its own shape field —
// uppStatus on a UPP, chargeOffStatus on a Charge Off — and either way the answer is
// whether "CPC" is part of the shape.
//
// A value already recorded keeps the group visible whatever the shape says, so data can
// never hide behind a shape nobody set: packages created before the picker existed have
// no shape at all, and an analyst who skips it shouldn't lose sight of figures already
// entered.
const cpcApplies = (f: Record<string, unknown>) => {
  const shape = String(f.uppStatus ?? f.chargeOffStatus ?? '');
  if (shape.includes('CPC')) return true;
  return CPC_KEYS.some(k => {
    const v = f[k];
    return v !== undefined && v !== null && v !== '';
  });
};

// Fields that only apply when the package's own data says so. The generic editor and
// the create forms both read this, so a rule written here holds everywhere.
export const FIELD_VISIBLE_WHEN: Partial<Record<keyof PackageFields, (f: Record<string, unknown>) => boolean>> = {
  // Only a UPP filed *without* a charge-off has a reason for not requesting one (#17).
  uppChargeOffNotRequestedReason: f => f.uppStatus === UPP_WITHOUT_CO,
  // The CPC group belongs to whichever package carries the claim. On a UPP that is the
  // "...with CO and CPC" shape only; on a Charge Off (no uppStatus at all) it is always
  // available, blank when no CPC applies.
  ...Object.fromEntries(CPC_KEYS.map(k => [k, cpcApplies])),
};


export const isFieldVisible = (k: keyof PackageFields, fields: Record<string, unknown>) =>
  FIELD_VISIBLE_WHEN[k]?.(fields) ?? true;

// Build a PackageFields object from a raw create/submit/decision-form record,
// keeping only keys valid for `type` (per PACKAGE_FIELD_KEYS) and coercing
// numeric/boolean values. Blank/undefined/null values are dropped.
export function buildPackageFieldsFromRaw(type: PackageType, raw: Record<string, unknown>): PackageFields {
  const keys = PACKAGE_FIELD_KEYS[type];
  const out: PackageFields = {};
  for (const k of keys) {
    const v = raw[k as string];
    if (v === undefined || v === null || v === '') continue;
    // Drop a conditional field whose condition no longer holds (e.g. Reason was
    // switched off 'Other' after its detail text had been typed).
    if (!isFieldVisible(k, raw)) continue;
    if (NUMERIC_FIELD_KEYS.has(k)) {
      const n = typeof v === 'number' ? v : parseFloat(String(v));
      if (!isNaN(n)) (out as Record<string, unknown>)[k] = n;
    } else if (BOOLEAN_FIELD_KEYS.has(k)) {
      (out as Record<string, unknown>)[k] = Boolean(v);
    } else {
      (out as Record<string, unknown>)[k] = v;
    }
  }
  return out;
}

// Humanize a PackageFields (or any camelCase) key for display, e.g.
// "datePurchaseSubmitted" -> "Date Purchase Submitted".
export function humanizeKey(k: string): string {
  return k.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase()).trim();
}

// Format a package field value for read-only display. Returns null for
// missing/blank values so callers can decide how to render "no value".
export function fmtFieldValue(v: unknown): string | null {
  if (v === undefined || v === null || v === '') return null;
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) return new Date(v).toLocaleDateString();
  return String(v);
}

// The most recent package of `type` for this loan, by submittedAt (string,
// ISO-comparable). Returns undefined if there is no package of that type.
export function latestPackageOfType<T extends { requestType: string | null; submittedAt: string }>(
  requests: T[], type: string,
): T | undefined {
  return requests
    .filter(r => r.requestType === type)
    .reduce<T | undefined>((latest, r) => (!latest || r.submittedAt > latest.submittedAt) ? r : latest, undefined);
}
